import React, { useContext, useEffect, useState } from 'react'
import { BillingoPartner, PostBillingoPartnerDto } from '../models/BillingoPartner'
import { useTranslation } from 'react-i18next';
import useBillingoPartnerValidation from '../hooks/billing/useBillingoPartnerValidation';
import useFetch from '../hooks/useFetch';
import { postUpsertBillingoPartner } from '../config/apiRoutes';
import UserContext from '../contexts/UserContext';
import SnackbarContext from '../contexts/SnackbarContext';
import StandardTextInput from '../inputs/StandardTextInput';
import SaveIcon from '@mui/icons-material/Save';
import PrimaryButton from '../buttons/PrimaryButton';
import SectionTitle from '../texts/SectionTitle';


type Props = {
    billingoPartner: BillingoPartner;
    onSave: (partner: BillingoPartner) => void;
}

const BillingoPartnerEditor = (props: Props) => {
    // This should be controlled by a state or prop in a real application
    const [billingoPartner, setBillingoPartner] = useState<BillingoPartner>(props.billingoPartner);
    const { t } = useTranslation();
    const { call, error, errorMessage } = useFetch();
    const { selectedRestaurant } = useContext(UserContext);
    const snackbar = useContext(SnackbarContext);
    const {
        isValidationEnabled,
        setIsValidationEnabled,
        isNameValid,
        isCityValid,
        isPostalCodeValid,
        isAddressValid,
        getBillingInfoValid
    } = useBillingoPartnerValidation(billingoPartner);
    const [emails, setEmails] = useState<string>("");

    async function handleSaveClick() {
        setIsValidationEnabled(true);
        if (!getBillingInfoValid()) return;
        if (selectedRestaurant == null) return;
        const response = await call<any, PostBillingoPartnerDto>(
            "POST",
            postUpsertBillingoPartner.replace(":restaurantShortName", selectedRestaurant.restaurantShortName),
            {
                name: billingoPartner.name,
                address: {
                    country_code: billingoPartner.address.country_code,
                    post_code: billingoPartner.address.post_code,
                    city: billingoPartner.address.city,
                    address: billingoPartner.address.address
                },
                phone: billingoPartner.phone,
                taxcode: billingoPartner.taxcode,
                emails: emails.split(",").map(email => email.trim())
            });
        if (response.ok && response.data) {
            snackbar.showMessage(t("common.changes_saved"), "success");
            props.onSave({ ...billingoPartner, emails: emails.split(",").map(email => email.trim()) });
            setIsValidationEnabled(false);
        }
    }

    useEffect(() => {
        if (error && errorMessage) {
            snackbar.showMessage(errorMessage, "error");
        }
    }, [error, errorMessage])

    useEffect(() => {
        setBillingoPartner(props.billingoPartner);
    }, [props.billingoPartner])

    function handleEmailChanged(emailString: string) {
        setEmails(emailString);
    }

    useEffect(() => {
        setEmails(props.billingoPartner.emails.join(","));
    }, [props.billingoPartner]);

    return (
        <div className="flex flex-col gap-3 p-3">
            <SectionTitle>
                <div className="text-center">
                    {t("payment.edit_billing_information")}
                </div>
            </SectionTitle>
            <div className="grid grid-cols-2 gap-3">
                <div className="md:col-span-1 col-span-2">
                    <StandardTextInput
                        label={t("payment.billing.name")}
                        value={billingoPartner.name}
                        onChange={(e) => { setBillingoPartner({ ...billingoPartner, name: e.target.value }) }}
                        required
                        helperText={t("common.validation.required")}
                        error={!isNameValid && isValidationEnabled}
                    />
                </div>
                <div className="md:col-span-1 col-span-2">
                    <StandardTextInput
                        label={t("payment.billing.tax_number")}
                        value={billingoPartner.taxcode}
                        onChange={(e) => { setBillingoPartner({ ...billingoPartner, taxcode: e.target.value }) }}
                        helperText={t("payment.billing.tax_number_helper_text")}
                    />
                </div>
            </div>
            <div className="grid grid-cols-2 ">
                <div className="md:col-span-1 col-span-2">
                    <StandardTextInput
                        label={t("payment.billing.country")}
                        value={t("common.hungary")}
                        disabled
                    />
                </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
                <div className="md:col-span-1 col-span-2">
                    <StandardTextInput
                        label={t("payment.billing.postal_code")}
                        value={billingoPartner.address.post_code}
                        onChange={(e) => { setBillingoPartner({ ...billingoPartner, address: { ...billingoPartner.address, post_code: e.target.value } }) }}
                        helperText={t("common.validation.required")}
                        error={!isPostalCodeValid && isValidationEnabled}
                    />
                </div>
                <div className="md:col-span-1 col-span-2">
                    <StandardTextInput
                        label={t("payment.billing.city")}
                        value={billingoPartner.address.city}
                        onChange={(e) => { setBillingoPartner({ ...billingoPartner, address: { ...billingoPartner.address, city: e.target.value } }) }}
                        helperText={t("common.validation.required")}
                        error={!isCityValid && isValidationEnabled}
                    />
                </div>
                <div className="md:col-span-1 col-span-2">
                    <StandardTextInput
                        label={t("payment.billing.address")}
                        value={billingoPartner.address.address}
                        onChange={(e) => { setBillingoPartner({ ...billingoPartner, address: { ...billingoPartner.address, address: e.target.value } }) }}
                        helperText={t("common.validation.required")}
                        error={!isAddressValid && isValidationEnabled}
                    />
                </div>
                <div className="md:col-span-1 col-span-2">
                    <StandardTextInput
                        label={t("payment.billing.phone")}
                        value={billingoPartner.phone}
                        onChange={(e) => { setBillingoPartner({ ...billingoPartner, phone: e.target.value }) }}
                        helperText={t("common.validation.optional")}
                    />
                </div>
                <div className="md:col-span-1 col-span-2">
                    <StandardTextInput
                        label={t("auth.email")}
                        value={emails}
                        onChange={(e) => { handleEmailChanged(e.target.value) }}
                        helperText={t("payment.billing.emails_helper_text")}
                    />
                </div>
            </div>
            <div className="mt-2">
                <PrimaryButton
                    onClick={handleSaveClick}
                    variant="primary"
                    startIcon={<SaveIcon />}
                    fullWidthOnMobile
                >
                    {t("common.save")}
                </PrimaryButton>
            </div>
        </div>
    )
}

export default BillingoPartnerEditor